"use client";

import { Format, getFormatDisplayName } from '../../lib/formats';
import { cn } from '@repo/ui/lib/utils';
import { ContentContainer } from './content-container';
import { CodeBlock, CodeBlockLine, SupernovaCodeBlock } from '@repo/ui';

interface HowToFilterProps {
  format: Format;
  className?: string;
}

interface FilterComponentProps {
  format: Format;
}

function PandasFilter({ format }: FilterComponentProps) {
  return (
    <>
      <p>First, we need to install pandas:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install pandas"}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Next, import pandas and load your {format.toLowerCase()} file:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`import pandas as pd`}</CodeBlockLine>
        {format === Format.CSV && <CodeBlockLine>{`df = pd.read_csv('path/to/file.csv')`}</CodeBlockLine>}
        {format === Format.TSV && <CodeBlockLine>{`df = pd.read_csv('path/to/file.tsv', sep='\\t')`}</CodeBlockLine>}
        {format === Format.PARQUET && <CodeBlockLine>{`df = pd.read_parquet('path/to/file.parquet')`}</CodeBlockLine>}
        {format === Format.XLSX && <CodeBlockLine>{`df = pd.read_excel('path/to/file.xlsx')`}</CodeBlockLine>}
        {format === Format.JSON && <CodeBlockLine>{`df = pd.read_json('path/to/file.json')`}</CodeBlockLine>}
      </SupernovaCodeBlock>
      
      <p>Apply a filter to your data. For example, to filter rows where a column named 'value' is greater than 100:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`filtered_df = df[df['value'] > 100]`}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>You can also apply multiple conditions using logical operators:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`# Filter rows where value > 100 AND category is 'A'`}</CodeBlockLine>
        <CodeBlockLine>{`filtered_df = df[(df['value'] > 100) & (df['category'] == 'A')]`}</CodeBlockLine>
        <CodeBlockLine>{`# Filter rows where value > 100 OR category is 'A'`}</CodeBlockLine>
        <CodeBlockLine>{`filtered_df = df[(df['value'] > 100) | (df['category'] == 'A')]`}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Finally, save the filtered data back to a file:</p>
      <SupernovaCodeBlock>
        {format === Format.CSV && <CodeBlockLine>{`filtered_df.to_csv('filtered_file.csv', index=False)`}</CodeBlockLine>}
        {format === Format.TSV && <CodeBlockLine>{`filtered_df.to_csv('filtered_file.tsv', sep='\\t', index=False)`}</CodeBlockLine>}
        {format === Format.PARQUET && <CodeBlockLine>{`filtered_df.to_parquet('filtered_file.parquet', index=False)`}</CodeBlockLine>}
        {format === Format.XLSX && <CodeBlockLine>{`filtered_df.to_excel('filtered_file.xlsx', index=False)`}</CodeBlockLine>}
        {format === Format.JSON && <CodeBlockLine>{`filtered_df.to_json('filtered_file.json', index=False)`}</CodeBlockLine>}
      </SupernovaCodeBlock>
    </>
  );
}

function DuckDBFilter({ format }: FilterComponentProps) {
  return (
    <>
      <p>First, we need to install duckdb:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install duckdb"}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Import DuckDB and set up your environment:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`import duckdb`}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Use SQL to filter your data directly from the file. For example, to filter rows where a column named 'value' is greater than 100:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`query = f"SELECT * FROM 'path/to/file.${format.toLowerCase()}' WHERE value > 100"`}</CodeBlockLine>
        <CodeBlockLine>{`result = duckdb.sql(query).fetchdf()`}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>You can use any SQL WHERE clause for more complex filtering:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`# Filter with multiple conditions`}</CodeBlockLine>
        <CodeBlockLine>{`query = f"SELECT * FROM 'path/to/file.${format.toLowerCase()}' WHERE value > 100 AND category = 'A'"`}</CodeBlockLine>
        <CodeBlockLine>{`result = duckdb.sql(query).fetchdf()`}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Save the filtered data to a new file:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`duckdb.sql(f"COPY result TO 'filtered_file.${format.toLowerCase()}'").fetchdf()`}</CodeBlockLine>
      </SupernovaCodeBlock>
    </>
  );
}

function ClickHouseFilter({ format }: FilterComponentProps) {
  return (
    <>
      <p>First, we need to install clickhouse-connect:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install clickhouse-connect"}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Import ClickHouse and set up a client connection:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`import clickhouse_connect`}</CodeBlockLine>
        <CodeBlockLine>{`client = clickhouse_connect.get_client(host='localhost', port=8123)`}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Use SQL to filter your data. For example, to filter rows where a column named 'value' is greater than 100:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`query = f"SELECT * FROM file('path/to/file.${format.toLowerCase()}') WHERE value > 100"`}</CodeBlockLine>
        <CodeBlockLine>{`result = client.query(query).result_set`}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>ClickHouse supports complex SQL filtering with multiple conditions:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`# Filter with multiple conditions and aggregation`}</CodeBlockLine>
        <CodeBlockLine>{`query = f"SELECT category, AVG(value) as avg_value FROM file('path/to/file.${format.toLowerCase()}') WHERE value > 100 GROUP BY category"`}</CodeBlockLine>
        <CodeBlockLine>{`result = client.query(query).result_set`}</CodeBlockLine>
      </SupernovaCodeBlock>
      
      <p>Save the filtered data to a new file:</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`client.command(f"SELECT * FROM file('path/to/file.${format.toLowerCase()}') WHERE value > 100 INTO OUTFILE 'filtered_file.${format.toLowerCase()}'")`}</CodeBlockLine>
      </SupernovaCodeBlock>
    </>
  );
}

export function HowToFilter({ format, className }: HowToFilterProps) {
  return (
    <ContentContainer className={cn("space-y-6", className)}>
      <h2 className="text-2xl font-semibold tracking-tight mb-4">{`How to filter ${getFormatDisplayName(format)} files in Python`}</h2>
      
      <p>
        Here are three effective ways to filter {getFormatDisplayName(format)} files in Python using different libraries. Each approach has its own advantages depending on your specific needs and file sizes.
      </p>
      
      <h3 className="text-xl font-semibold tracking-tight mt-6">{`Filtering ${getFormatDisplayName(format)} files with Pandas`}</h3>
      <p>Pandas provides a straightforward approach for filtering files and works well for most common data tasks:</p>
      <PandasFilter format={format} />
      
      <h3 className="text-xl font-semibold tracking-tight mt-6">{`Filtering ${getFormatDisplayName(format)} files with DuckDB`}</h3>
      <p>DuckDB is an in-process SQL OLAP database that's perfect for larger files and analytical workloads:</p>
      <DuckDBFilter format={format} />
      
      <h3 className="text-xl font-semibold tracking-tight mt-6">{`Filtering ${getFormatDisplayName(format)} files with ClickHouse`}</h3>
      <p>ClickHouse is a high-performance column-oriented database system that's excellent for large-scale data processing:</p>
      <ClickHouseFilter format={format} />
    </ContentContainer>
  );
} 